import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box } from '@mui/material';
import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface Props {
  latitude: string;
  longitude: string;
  radius: string;
  disabled: boolean;
  onSelect: (latitude: number, longitude: number) => void;
}

export default function GateLocationMap(props: Props) {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map>();
  const marker = useRef<L.CircleMarker>();
  const area = useRef<L.Circle>();
  const current = useRef(props);
  current.current = props;
  const [tileError, setTileError] = useState(false);

  useEffect(() => {
    if (!container.current) return;
    const view = L.map(container.current, { scrollWheelZoom: false, doubleClickZoom: false }).setView([20, 0], 2);
    map.current = view;
    const streets = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).on('tileerror', () => setTileError(true)).on('load', () => view.invalidateSize()).addTo(view);
    const satellite = L.tileLayer('https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      maxZoom: 19,
      attribution: 'Source: <a href="https://www.arcgis.com/home/item.html?id=10df2279f9684e4a9f6a7f08febac2a9">Esri</a>, Vantor, Earthstar Geographics, and the GIS User Community',
    }).on('tileerror', () => setTileError(true));
    L.control.layers({ Streets: streets, Satellite: satellite }, undefined, { collapsed: false }).addTo(view);
    view.on('baselayerchange', () => setTileError(false));
    view.on('click', (event: L.LeafletMouseEvent) => {
      if (current.current.disabled) return;
      const point = event.latlng.wrap();
      current.current.onSelect(Number(point.lat.toFixed(6)), Number(point.lng.toFixed(6)));
    });
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(() => view.invalidateSize());
    observer?.observe(container.current);
    return () => {
      observer?.disconnect();
      view.remove();
      map.current = undefined;
      marker.current = undefined;
      area.current = undefined;
    };
  }, []);

  useEffect(() => {
    const view = map.current;
    if (!view) return;
    const lat = Number(props.latitude);
    const lng = Number(props.longitude);
    const valid = props.latitude.trim() && props.longitude.trim() &&
      Number.isFinite(lat) && Math.abs(lat) <= 90 && Number.isFinite(lng) && Math.abs(lng) <= 180;
    if (!valid) {
      marker.current?.remove();
      area.current?.remove();
      marker.current = undefined;
      area.current = undefined;
      return;
    }
    const position: L.LatLngTuple = [lat, lng];
    if (!marker.current) {
      marker.current = L.circleMarker(position, { radius: 7, color: '#fff', weight: 2, fillColor: '#1565c0', fillOpacity: 1, interactive: false }).addTo(view);
      view.setView(position, Math.max(view.getZoom(), 16));
    } else if (!marker.current.getLatLng().equals(position)) {
      marker.current.setLatLng(position);
      view.panTo(position);
    }
    const meters = Number(props.radius);
    if (Number.isFinite(meters) && meters >= 100 && meters <= 1000) {
      if (!area.current) {
        area.current = L.circle(position, { radius: meters, color: '#1565c0', weight: 2, fillOpacity: 0.12, interactive: false }).addTo(view);
      } else {
        area.current.setLatLng(position).setRadius(meters);
      }
    } else {
      area.current?.remove();
      area.current = undefined;
    }
  }, [props.latitude, props.longitude, props.radius]);

  return <Box>
    <Box ref={container} role="region" aria-label="Gate location map" aria-busy={props.disabled}
      sx={{ height: { xs: 300, sm: 400 }, width: '100%', zIndex: 0, borderRadius: 1, cursor: props.disabled ? 'wait' : 'crosshair' }} />
    {tileError && <Alert severity="warning">Map tiles could not be loaded. You can still enter coordinates or use your current location.</Alert>}
  </Box>;
}
